
import { useContext, useState, useEffect } from 'react';
import { Link, Outlet } from "react-router-dom";
import { styled, ThemeProvider } from '@mui/material/styles';

import { appTheme } from '../styles/theme';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MuiDrawer from '@mui/material/Drawer';

import { AppMenuItems } from './menuItems';

import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';

import BrandMark from '../components/BrandMark';

import ApplicationContext from '../contexts/application';
import CustomDialog from '../components/CustomDialog';
import { Snackbar, useMediaQuery } from '@mui/material';

import { UserRoles } from '../libs/userRoles';

/*
 * Defined at module scope, deliberately.
 *
 * These used to be created inside Layout(), which meant every render produced brand-new
 * AppBar and Drawer component types. React compares element types by identity, so a new
 * type at the same position is not a re-render - it unmounts the old subtree and mounts a
 * fresh one. AppMenuItems sits inside this Drawer and keeps its expanded sections in local
 * state, so an expanded menu closed itself every time.
 *
 * Layout consumes ApplicationContext, which updates on every MQTT message, so in practice
 * the menu collapsed whenever an alarm arrived.
 *
 * createTheme was in there too, rebuilding the theme and defeating MUI's memoisation on
 * each render. Nothing here closes over state at all, so all of it belongs
 * out here. The theme has since moved to styles/theme.js, which keeps that property for the
 * same reason: it is a module constant, built once.
 */

/*
 * The bar starts where the rail ends, rather than running over the top of it.
 *
 * Full width it covered the rail's own header, which is where the product mark sits - the
 * design keeps that corner for the mark and starts the bar beside it.
 */
/*
 * One width. The rail no longer expands, so there is no transition to run and no second layout
 * to keep right - which is what the design has, and what removes the two states the narrow one
 * used to be the poor relation of.
 */
const RAIL_WIDTH = 88;

const AppBar = styled(MuiAppBar)(() => ({
    marginLeft: RAIL_WIDTH,
    width: `calc(100% - ${RAIL_WIDTH}px)`,
}));

const Drawer = styled(MuiDrawer)(
    ({ theme }) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: RAIL_WIDTH,
            boxSizing: 'border-box',
            overflowX: 'hidden',
        },
    }),
);

function Layout() {
    const { settings, dialogMessage, showDialog, clearDialog,
        isError, badgeCount,
        snackbar, snackbarMessage, setShowSnackbar } = useContext(ApplicationContext);

    const mediumScreen = useMediaQuery('(min-height: 900px)');

    /*
     * Which of the client's two marks the rail shows.
     *
     * A deployment mounts both: logo.svg in its own colours, and logo-white.svg for putting on
     * something dark. The login screen already chooses between them that way - the colour one on
     * its white panel, the white one over the artwork - and the rail simply never did, because
     * until this theme the rail was always white.
     *
     * It is dark now, and MHAC's colour mark carries a #232935 navy that all but disappears
     * against it. Taken from the theme rather than from the setting that chose the theme, so it
     * follows the surface the mark actually sits on.
     */
    const railMark = appTheme.palette.mode === 'dark'
        ? '/branding/logo-white.svg'
        : '/branding/logo.svg';

    const [snackbarDuration, setSnackbarDuration] = useState(2000);
    const [userRoles, setUserRoles] = useState('');


    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setShowSnackbar(false);
    };

    useEffect(() => {
        if (Object.keys(settings).length > 0) {
            const admin = settings.roles && (
                settings.roles?.includes(UserRoles.ADMIN.label) ||
                settings.roles?.includes(UserRoles.OEM.label) ||
                settings.roles?.includes(UserRoles.OPERATOR.label)
            );

            if (!admin) {
                if (settings.roles?.length > 0) {
                    let groups = settings.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1));
                    let names = '(' + groups.join(', ') + ')';

                    // TODO: replace this hack
                    names = names.replace('Eh_fm', 'EH FM').replace('Cahill_fm', 'Company FM').replace('It', 'IT');

                    setUserRoles(names);
                }
                else {
                    setUserRoles('(No roles assigned)');
                }
            }
        }
    }, [settings]);

    return (
        <>
            <ThemeProvider theme={appTheme}>
                <Box sx={{ display: 'flex' }}>
                    <CssBaseline />
                    <AppBar position="absolute" style={{ boxShadow: "none" }}>
                        <Toolbar
                            sx={{
                                pr: '24px', // keep right padding when drawer closed
                            }}
                        >
                            {/*
                              * No control here any more. There is one rail at one width, so
                              * there is nothing to open or close - which is the point of the
                              * arrangement rather than a thing lost with it.
                              */}
                            <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                sx={{ flexGrow: 1 }}
                            >
                                TwinWorX ESB Console{' '}
                                <Typography component="span" variant="h6" sx={{ opacity: 0.65 }}>
                                    {userRoles}
                                </Typography>
                            </Typography>
                            {/*
                              * Label quiet, value full strength - the pairing the design uses
                              * across its top bar, where "Location" sits muted above the site
                              * name. Both were the same weight and colour here, so the bar read
                              * as one sentence rather than as a heading and a couple of facts.
                              *
                              * Dimmed with opacity rather than by naming a colour. This text sits
                              * on the bar, not on the page, so the only colour that is right in
                              * both themes is whatever the bar's own is - inherited, and stepped
                              * down from. Naming the page's text scale here is what put black
                              * lettering on the light theme's blue bar.
                              *
                              * The opacity is on the label rather than the parent, because nested
                              * opacity multiplies: dimming the whole line and brightening the
                              * name back up inside it cannot work.
                              */}
                            <Typography color="inherit" sx={{ mr: 2 }}>
                                <Typography component="span" sx={{ opacity: 0.65 }}>
                                    Welcome,{' '}
                                </Typography>
                                {settings.userName}
                            </Typography>
                            <IconButton color="inherit" component={Link} to={"/user"}>
                                <Badge color="error" badgeContent={badgeCount}>
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <Drawer variant="permanent">
                        <Toolbar
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 0,
                            }}
                        >
                            <Container component="div" style={{ width: "100%", display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }}>
                                <img src="logo.png" alt="twinworx logo" style={{ width: "30px", height: "30px" }} />
                                {/* <img src="/branding/logo.svg" alt="" style={{ width: '110px', pl: 2, display: mediumScreen ? 'none' : 'display' }} /> */}

                            </Container>
                        </Toolbar>
                        <Divider />
                        <AppMenuItems />

                        {/*
                          * The client's mark at the foot of the rail, turned on its side when the
                          * rail narrows to icons - the arrangement MHAC has always had. The
                          * consolidation commented this out and it has not rendered since.
                          *
                          * In the flow rather than position: fixed, which is how it was. Fixed
                          * took it out of the layout and floated it at the bottom of the window,
                          * which was fine while nothing else was down there - now that signing
                          * out sits at the foot of the rail, a floating mark would be laid over
                          * the top of it.
                          *
                          * Only when the window is tall enough to spare the room, as before.
                          *
                          * BrandMark rather than a bare img: the file arrives from a deployment's
                          * mounted branding directory and a site that mounts none should show no
                          * mark, not a broken-image icon.
                          */}
                        <Box sx={{
                            // Takes the empty space, so the rail's items and its way out stay
                            // together at the top and this stays at the foot.
                            mt: 'auto',
                            display: mediumScreen ? 'flex' : 'none',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            py: 2,
                            /*
                             * A rotated element still occupies its unrotated box in the layout,
                             * so a mark 180 wide and 40 tall reserves 40 of height however it is
                             * turned - and the turned mark needs 180. The rail is told to keep
                             * that much while it is narrow, or the mark is cut off at both ends.
                             */
                            height: 200,
                        }}>
                            <BrandMark
                                className="logo-vertical"
                                style={{ width: '180px' }}
                                src={railMark}
                            />
                        </Box>
                    </Drawer>
                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            height: '100vh',
                            /*
                             * The scroll happens one level in, not here.
                             *
                             * This element is the full height of the window and starts at the top
                             * of it, so when it scrolled, its scrollbar ran the whole height too -
                             * and the app bar, which is positioned over the top of it, covered the
                             * first sixty-odd pixels of the track. Scrolled to the top, where the
                             * thumb is, that is the part you cannot see.
                             *
                             * The Toolbar below is a spacer standing in for the app bar's height.
                             * It used to sit inside the scrolling area and scroll away with the
                             * content, which is what let the track start underneath the bar. Out
                             * here it holds the scrolling box down to below the bar, so the track
                             * begins where the content does.
                             */
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Toolbar />
                        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                            <Container maxWidth="false" sx={{ mt: 2, mb: 2, minWidth: 600 }}>
                                <Outlet />
                            </Container>
                        </Box>
                    </Box>

                    <CustomDialog open={showDialog} title="TwinWorX ESB"
                        content={dialogMessage} isError={isError}
                        onOkClick={clearDialog} />

                    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        open={snackbar}
                        onClose={handleSnackbarClose}
                        autoHideDuration={snackbarDuration}
                        message={snackbarMessage}
                    />
                </Box>
            </ThemeProvider>

        </>
    )
}

export default Layout;